import React, { useEffect, useRef, useState } from "react";
import { createChart, LineSeries, AreaSeries, ColorType } from "lightweight-charts";

const COLORS = ["#788CE3", "#17243F", "#92BAD5"];
const METRICS = [
  { key: "uses", label: "Sessions" },
  { key: "minutes", label: "Hours of use" },
  { key: "sessions", label: "Prompts/queries" },
];
const RANGES = [
  { key: "1W", days: 7 }, { key: "1M", days: 30 }, { key: "3M", days: 90 },
  { key: "6M", days: 180 }, { key: "1Y", days: 365 }, { key: "All", days: 9999 },
];

export function AdoptionChart({ series, testid = "adoption-chart" }) {
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const tipRef = useRef(null);
  const [metric, setMetric] = useState("uses");
  const [area, setArea] = useState(false);
  const [range, setRange] = useState("3M");
  const [hidden, setHidden] = useState({});
  const visible = (series || []).filter((s) => !hidden[s.app_id]).slice(0, 3);

  // Only offer ranges that actually differ given how much history exists.
  const allTimes = (series || []).flatMap((s) => (s.points || []).map((p) => new Date(p.date).getTime())).filter((t) => !Number.isNaN(t));
  const spanDays = allTimes.length ? (Date.now() - Math.min(...allTimes)) / 86400000 : 0;
  const availableRanges = [...RANGES.filter((r) => r.key !== "All" && r.days < spanDays * 0.95), { key: "All", days: 9999 }];
  const rangeKeys = availableRanges.map((r) => r.key);
  const effRange = rangeKeys.includes(range) ? range : rangeKeys[Math.max(0, rangeKeys.length - 2)] || "All";

  useEffect(() => {
    if (!elRef.current) return;
    const chart = createChart(elRef.current, {
      height: 300,
      localization: { locale: "en-US" },
      layout: { background: { type: ColorType.Solid, color: "#FFFFFF" }, textColor: "#5B6577", fontFamily: "Plus Jakarta Sans", attributionLogo: false },
      grid: { horzLines: { color: "#E3DED0" }, vertLines: { visible: false } },
      rightPriceScale: { borderColor: "#E3DED0" },
      timeScale: { borderColor: "#E3DED0" },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;
    const ro = new ResizeObserver(() => chart.applyOptions({ width: elRef.current.clientWidth }));
    ro.observe(elRef.current);
    chart.applyOptions({ width: elRef.current.clientWidth });
    return () => { ro.disconnect(); chart.remove(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    // clear existing series
    (chart.__series || []).forEach((s) => chart.removeSeries(s));
    chart.__series = [];
    const names = {};
    const cut = (RANGES.find((r) => r.key === effRange) || { days: 9999 }).days;
    const now = Date.now();
    visible.forEach((s, i) => {
      const color = COLORS[i % COLORS.length];
      const ser = chart.addSeries(area ? AreaSeries : LineSeries, {
        color, lineWidth: 2,
        topColor: area ? "rgba(120,140,227,0.25)" : undefined,
        bottomColor: area ? "rgba(120,140,227,0.0)" : undefined,
      });
      const data = s.points
        .filter((p) => (now - new Date(p.date).getTime()) / 86400000 <= cut)
        .map((p) => ({ time: p.date, value: metric === "minutes" ? Math.round((p.minutes / 60) * 10) / 10 : p[metric] }));
      ser.setData(data);
      names[color] = s.app;
      chart.__series.push(ser);
    });
    chart.timeScale().fitContent();

    const handler = (param) => {
      const tip = tipRef.current;
      if (!tip) return;
      if (!param.time || !param.point) { tip.style.display = "none"; return; }
      tip.textContent = "";
      const head = document.createElement("div");
      head.style.cssText = "font-size:11px;color:#5B6577;margin-bottom:2px";
      head.textContent = String(param.time);
      tip.appendChild(head);
      (chart.__series || []).forEach((ser, i) => {
        const v = param.seriesData.get(ser);
        if (v && v.value !== undefined) {
          const row = document.createElement("div");
          row.style.cssText = "display:flex;gap:6px;align-items:center";
          const dot = document.createElement("span");
          dot.style.cssText = `width:8px;height:8px;border-radius:9px;background:${COLORS[i % COLORS.length]}`;
          row.appendChild(dot);
          row.appendChild(document.createTextNode(`${visible[i]?.app ?? ""}: `));
          const strong = document.createElement("strong");
          strong.textContent = String(v.value);
          row.appendChild(strong);
          tip.appendChild(row);
        }
      });
      tip.style.display = "block";
      tip.style.left = Math.min(param.point.x + 16, elRef.current.clientWidth - 150) + "px";
      tip.style.top = param.point.y + "px";
    };
    chart.subscribeCrosshairMove(handler);
    return () => chart.unsubscribeCrosshairMove(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, area, effRange, visible.map((s) => s.app_id).join(","), series]);

  if (!series || series.length === 0) {
    return <p className="text-[14px] text-nb-ink2" data-testid={testid}>No usage recorded in this window.</p>;
  }

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 rounded-[10px] p-0.5" style={{ background: "var(--bg-sunken)" }}>
          {availableRanges.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
              className="text-[12px] px-2.5 py-1 rounded-md font-medium transition-colors"
              style={effRange === r.key ? { background: "#fff", color: "var(--brand-navy)" } : { color: "var(--text-muted)" }}>{r.key}</button>
          ))}
        </div>
        <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="metric-switch"
          className="h-9 rounded-[10px] text-[13px] px-2 bg-nb-surface" style={{ border: "1px solid var(--border-control)" }}>
          {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <button onClick={() => setArea((a) => !a)} data-testid="chart-type"
          className="h-9 px-3 rounded-[10px] text-[13px] bg-nb-surface" style={{ border: "1px solid var(--border-control)" }}>{area ? "Area" : "Line"}</button>
      </div>

      <div className="relative">
        <div ref={elRef} className="w-full" />
        <div ref={tipRef} className="absolute z-10 pointer-events-none rounded-lg px-2.5 py-1.5 text-[12px]"
          style={{ display: "none", background: "#fff", border: "1px solid #E3DED0", boxShadow: "0 8px 32px rgba(23,36,63,0.16)", color: "#17243F" }} />
      </div>

      <div className="flex flex-wrap gap-3 mt-3">
        {(series || []).slice(0, 6).map((s, i) => {
          const idx = visible.findIndex((v) => v.app_id === s.app_id);
          const on = !hidden[s.app_id];
          return (
            <button key={s.app_id} onClick={() => setHidden((h) => ({ ...h, [s.app_id]: on }))}
              data-testid={`legend-${s.app_id}`}
              className="inline-flex items-center gap-1.5 text-[12px]" style={{ opacity: on ? 1 : 0.4 }}>
              <span className="w-3 h-0.5 rounded" style={{ background: on && idx >= 0 ? COLORS[idx % COLORS.length] : "#8A91A0" }} />
              {s.app}
            </button>
          );
        })}
      </div>
    </div>
  );
}
